import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { VFlex } from '@/components/base';
import ScrollView from '@/components/Common/ScrollView';
import { SectionTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import DnDFileUploader from '@/features/CsvImport/DnDFileUploader';
import { DownloadSampleCSV } from '@/features/CsvImport/DownloadSampleCSV';
import FieldTable, { type DocumentRow } from '@/features/CsvImport/FieldTable';
import UploadPreview, {
  type SimpleCol,
  type SimpleRow,
  UPLOAD_STATE,
} from '@/features/CsvImport/UploadPreview';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useToastStore from '@/stores/pure/ToastStore';
import { FEEDBACK_BODY_TYPE, SOURCE_TYPE } from '@/types/feedbackConst';
import { getTimezoneDayjs, readableFormat } from '@/utils/time';
import { track } from '@/utils/track/track';

const ESSENTIALFIELDS = [['raw_text', 'chat_text']];

type Props = {
  closeUploadModal: () => void;
};

type FieldDocData = SimpleCol & DocumentRow;

const commonColumns: FieldDocData[] = [
  {
    columnKey: 'raw_text',
    colWidth: 400,
    type: 'string',
    required: true,
    desc: 'Feedback content',
  },
  {
    columnKey: 'title',
    colWidth: 200,
    type: 'string',
    desc: 'The title of the feedback.',
  },
  {
    columnKey: 'tags',
    colWidth: 200,
    type: 'string',
    desc: 'A list of tags that you want to assign to the feedback. The tags can be categories or simple tags that can be used to classify the feedback. Tags can have a tree structure, with up to 3 levels. You can use/ as a delimiter to specify a tree from the root to the leaf.',
  },
  {
    columnKey: 'date',
    colWidth: 80,
    render: (data) => (data ? readableFormat(data) : ''),
    type: 'string',
    desc: 'The date the feedback is created. It must be MM/DD/YYYY format.',
  },
  {
    columnKey: 'account',
    colWidth: 145,
    type: 'string',
    desc: 'The name of the account. If no account key is set, then if a account with the same name exists in the Syncly, it will be mapped to that account.',
  },
  {
    columnKey: 'account_key',
    colWidth: 160,
    type: 'string',
    desc: 'The key of the account. If a account with the same key already exists in Syncly, it will be mapped to that account.',
  },
  {
    columnKey: 'contact',
    colWidth: 145,
    type: 'string',
    desc: 'The name of the contact.',
  },
  {
    columnKey: 'contact_email',
    colWidth: 160,
    type: 'string',
    desc: 'The email of the contact. If a contact with the same email already exists in Syncly, it will be mapped to that contact.',
  },
  {
    columnKey: 'contact_key',
    colWidth: 145,
    type: 'string',
    desc: 'The key of the contact. If a contact with the same key already exists in Syncly, it will be mapped to that contact.',
  },
  {
    columnKey: 'agent_email',
    colWidth: 160,
    type: 'string',
    desc: 'The email of the agent.',
  },
  {
    columnKey: 'remarks',
    colWidth: 100,
    type: 'string',
    desc: 'The remarks associated with that feedback.',
  },
  {
    columnKey: 'data_origin',
    colWidth: 145,
    type: 'string',
    desc: 'The value indicating the original source through which the customer initially submitted the feedback.',
  },
  {
    columnKey: 'link',
    colWidth: 160,
    type: 'string',
    desc: 'Related link with the feedback.',
  },
  {
    columnKey: 'language',
    colWidth: 80,
    type: 'string',
    desc: 'The language of the feedback.',
  },
  {
    columnKey: 'checksum',
    colWidth: 80,
    type: 'string',
    desc: 'The checksum of the feedback. This field is used to prevent duplicate feedbacks.',
  },
  {
    columnKey: 'priority',
    colWidth: 100,
    type: 'string',
    desc: 'Priority of the feedback, 1: Not Assigned, 2: Low, 3: Medium, 4: High, 5: Urgent',
  },
  {
    columnKey: 'rating_score',
    colWidth: 80,
    type: '0 ~ 10',
    desc: 'The numerical score given by the customer along with their feedback content. This score can range from 0 to 10 (integer values only).',
  },
  {
    columnKey: 'nps_score',
    colWidth: 80,
    type: '0 ~ 10',
    desc: 'The numerical score given by the customer along with their feedback content from NPS survey. This score can range from 0 to 10 (integer values only).',
  },
  {
    columnKey: 'ces_score',
    colWidth: 80,
    type: '0 ~ 10',
    desc: 'The numerical score given by the customer along with their feedback content from CES survey. This score can range from 0 to 10 (integer values only).',
  },
  {
    columnKey: 'csat_score',
    colWidth: 80,
    type: '0 ~ 10',
    desc: 'The numerical score given by the customer along with their feedback content from CSAT survey. This score can range from 0 to 10 (integer values only).',
  },
];

const FeedbackCsvUploadBody = (props: Props) => {
  const color = useColor();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  const [errorMsg, setErrorMsg] = useState('');
  const [mountComplete, setMountComplete] = useState(false);
  const [uploading, setUploading] = useState<UPLOAD_STATE>(UPLOAD_STATE.READY);
  const [progress, setProgress] = useState({
    current: 1,
    total: 100,
  });
  const [dragActive, setDragActive] = useState(false);
  const [formattedData, setFormattedData] = useState<CsvFeedbackParams[]>([]);

  const onComplete = () => {
    setMountComplete(true);
    addDefaultToast('Successfully Completed', 'success');
  };

  const onFail = () => {
    setMountComplete(false);
    addDefaultToast('Upload Fail', 'fail');
  };

  const uploadData = async (
    data: Record<keyof CsvFeedbackParams, string>[],
    fileFields: string[],
  ) => {
    try {
      ESSENTIALFIELDS.forEach((field) => {
        if (
          Array.isArray(field) &&
          field.filter((v) => fileFields.includes(v)).length == 0
        ) {
          throw new Error(
            'Some of your data is formatted in a way we are not familiar with. Check the headers and try again!',
          );
        } else if (typeof field === 'string' && !fileFields?.includes(field)) {
          throw new Error(
            'Some of your data is formatted in a way we are not familiar with. Check the headers and try again!',
          );
        }
      });

      data.forEach((data) => {
        if (data.raw_text === '') {
          throw new Error(
            'raw_text must be filled. Check the contents and try again!',
          );
        }
      });

      setFormattedData(
        data.reduce((acc, feedback) => {
          const params: CsvFeedbackParams = {
            date: feedback.date
              ? getTimezoneDayjs(feedback.date).toJSON()
              : undefined,
            raw_text: feedback.raw_text || '',
            tags: feedback.tags ? feedback.tags.split(',') : undefined,
            remarks: feedback.remarks ? feedback.remarks.split(',') : undefined,
            data_origin: feedback.data_origin,
            rating_score: feedback.rating_score
              ? Number(feedback.rating_score)
              : undefined,
            nps_score: feedback.nps_score
              ? Number(feedback.nps_score)
              : undefined,
            ces_score: feedback.ces_score
              ? Number(feedback.ces_score)
              : undefined,
            csat_score: feedback.csat_score
              ? Number(feedback.csat_score)
              : undefined,
            link: feedback.link,
            title: feedback.title,
            priority: feedback.priority,
            checksum: feedback.checksum,
            account: feedback.account,
            account_key: feedback.account_key,
            contact: feedback.contact,
            contact_email: feedback.contact_email,
            contact_key: feedback.contact_key,
            agent_email: feedback.agent_email,
          };

          acc.push(params);
          return acc;
        }, [] as CsvFeedbackParams[]),
      );

      setErrorMsg('');
      onComplete();
    } catch (e: any) {
      setErrorMsg(e.message);
      onFail();
    }
  };

  const confirmUpload = async () => {
    setUploading(UPLOAD_STATE.UPLOADING);
    setProgress((state) => ({
      ...state,
      total: formattedData.length,
    }));
    try {
      const chunkSize = 50;

      const mappedData: JSONFeedbackUploadParams[] = formattedData.map(
        (feedback) => {
          const params: JSONFeedbackUploadParams = {
            date: feedback.date ?? undefined,
            raw_text: feedback.raw_text ?? undefined,
            tags: feedback.tags ?? undefined,
            remarks: feedback.remarks ?? undefined,
            data_origin: feedback.data_origin ?? undefined,
            rating_score: feedback.rating_score ?? undefined,
            nps_score: feedback.nps_score ?? undefined,
            ces_score: feedback.ces_score ?? undefined,
            csat_score: feedback.csat_score ?? undefined,
            link: feedback.link || undefined,
            title: feedback.title || undefined,
            priority: feedback.priority || undefined,
            source: {
              type: SOURCE_TYPE.CSV,
              imported_at: new Date().toJSON(),
            checksum: feedback.checksum || undefined,
            },
            body_type: FEEDBACK_BODY_TYPE.PLAIN_TEXT,
          };

          if (feedback.account || feedback.account_key) {
            params.account = {
              name: feedback.account || undefined,
              key: feedback.account_key || undefined,
            };
          }
          if (
            feedback.contact ||
            feedback.contact_email ||
            feedback.contact_key
          ) {
            params.contact = {
              name: feedback.contact || undefined,
              email: feedback.contact_email || undefined,
              key: feedback.contact_key || undefined,
            };
          }
          if (feedback.agent_email) {
            params.manager = {
              email: feedback.agent_email || undefined,
            };
          }

          return params;
        },
      );
      for (let i = 0; i < mappedData.length; i += chunkSize) {
        const chunk = mappedData.slice(i, i + chunkSize);
        try {
          await service.feedback.uploadFeedback(chunk);
          setProgress((state) => ({
            ...state,
            current: state.current + chunkSize,
          }));
        } catch (e) {
          alert(
            `Upload failed at chunk ${Math.floor(
              i / chunkSize,
            )}. Please try again starting from row ${i}.`,
          );
          throw e;
        }
      }
      setUploading(UPLOAD_STATE.COMPLETE);

      // Track feedback upload complete event
      track(AMP_KEYS.FEEDBACK_UPLOAD_COMPLETE, {
        feedback_count: mappedData.length,
        upload_type: 'csv',
      });

      addDefaultToast('The file has been successfully uploaded', 'success');
    } catch (e) {
      setUploading(UPLOAD_STATE.READY);
      addDefaultToast('Upload Fail', 'fail');
    }
  };

  const displayColumns = [...commonColumns];

  const previewData: SimpleRow[] = useMemo(() => {
    return formattedData.slice(0, 100).map((row, index) => {
      return {
        rowKey: index,
        rowData: Object.entries(row).map(([key, value]) => {
          return {
            columnKey: key,
            rawData: value?.toString() || '',
          };
        }),
      };
    });
  }, [formattedData]);

  if (mountComplete)
    return (
      <UploadPreview
        columns={displayColumns}
        total={formattedData.length}
        datas={previewData}
        progress={progress}
        uploading={uploading}
        confirmUpload={confirmUpload}
        closeUploadModal={props.closeUploadModal}
      />
    );

  return (
    <ScrollView style={{ height: '100%' }}>
      <Wrapper g={20}>
        {errorMsg && (
          <Typo weight="sb" color={color.red600} style={{ height: 24 }}>
            {errorMsg}
          </Typo>
        )}

        <DownloadSampleCSV
          description={
            <>
              You can upload your feedback using a csv.
              <br />
              See the table below for information on the feedback fields you can
              upload.
            </>
          }
          href="/assets/sample_csv.csv"
          fileName="Syncly CSV import Sample.csv"
        />

        <DnDFileUploader
          uploadData={uploadData}
          dragActive={dragActive}
          setDragActive={setDragActive}
        />

        <VFlex g={8}>
          <SectionTitle>Feedback field description</SectionTitle>
          <FieldTable rows={displayColumns} />
        </VFlex>
      </Wrapper>
    </ScrollView>
  );
};

export default FeedbackCsvUploadBody;

const Wrapper = styled(VFlex)`
  padding: 16px 32px;
`;
